import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HOME_ROUTE } from '../../../lib/constants/route';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import { useTranslation } from 'react-i18next';
import SettingsCommonCard from '../../../ui/cards/SettingsCommonCard';
import {
  anthropicSVG,
  googleMapsSVG,
  keySVG,
  openAISVG,
  plusSignSVG,
  questionSVG,
  simpleCreditCardSVG,
  trash2SVG,
  warningWithCircleSVG,
} from '../../../lib/allCustomSVGs';
import BlurAndCopyInput from '../../../ui/inputs/BlurAndCopyInput';
import KeySVG from '../../../ui/customSVGs/KeySVG';
import SecretSVG from '../../../ui/customSVGs/SecretSVG';
import '../../../styles/pages/settings/settings.scss';
import '../../../styles/generic/normalForm.scss';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import truncate from '../../../lib/helper-functions/truncate';
import AINormalInput from '../../../ui/inputs/AINormalInput';
import Popup from '../../../ui/popup/Popup';
import RadioButton from '../../../ui/buttons/RadioButton';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import get_adup_api_keys_api_call, {
  create_adup_api_key_api_call,
  delete_adup_api_key_api_call,
  AdUpApiKey,
  CreateAdUpApiKeyRequest,
  CreatedAdUpApiKey,
} from '../../../lib/api/settings/adup-api-keys-api-call';
import get_external_api_types_api_call, {
  get_external_api_keys_api_call,
  create_external_api_key_api_call,
  update_external_api_key_api_call,
  delete_external_api_key_api_call,
  ExternalApiType,
  ExternalApiKey,
  CreateExternalApiKeyRequest,
  UpdateExternalApiKeyRequest,
  getExternalApiKeyMask,
  getExternalApiKeyReplacement,
  EXTERNAL_API_KEY_MASK_DOTS,
} from '../../../lib/api/settings/external-api-keys-api-call';
import HELP_LINKS from '../../../lib/constants/helpLinks';
import makeFirstLetterCapital from '../../../lib/helper-functions/makeFirstLetterCapital';
import { useEmployeeRole } from '../../../lib/rbac/useEmployeeRole';

/**
 * The page where /my-mcp-setup hands out the personal `emp_` key the Claude
 * connector needs. The AdUp keys on this page are a different thing entirely.
 */
const MCP_SETUP_PATH = '/my-mcp-setup';

const APIKeySettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Read-only members may not create API keys (the server refuses it), so
  // the New API Key button is not offered to them.
  const canCreateApiKey = useEmployeeRole() !== 'read_only';
  const [isAddAPIKeyPopupOpen, setIsAddAPIKeyPopupOpen] = useState(false);
  const [isDeleteAPIKeyPopupOpen, setIsDeleteAPIKeyPopupOpen] = useState(false);
  const [apiKeys, setApiKeys] = useState<AdUpApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedKeyForDeletion, setSelectedKeyForDeletion] =
    useState<AdUpApiKey | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPermissions, setNewKeyPermissions] = useState<{
    read: boolean;
    write: boolean;
  }>({
    read: true,
    write: false,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // The secret of a just-created key. The API returns it only once, so it is
  // shown until the user closes the popup and then dropped from state.
  const [createdKey, setCreatedKey] = useState<CreatedAdUpApiKey | null>(null);

  const [externalApiTypes, setExternalApiTypes] = useState<ExternalApiType[]>(
    []
  );
  const [externalApiKeys, setExternalApiKeys] = useState<ExternalApiKey[]>([]);
  const [externalLoading, setExternalLoading] = useState(true);
  const [externalKeyValues, setExternalKeyValues] = useState<{
    [key: string]: string;
  }>({});
  const [isSavingExternal, setIsSavingExternal] = useState(false);
  const [isDeletingExternal, setIsDeletingExternal] = useState(false);
  const [selectedExternalKeyForDeletion, setSelectedExternalKeyForDeletion] =
    useState<ExternalApiKey | null>(null);

  const toggleAddAPIKeyPopupModal = (value: boolean) => {
    setIsAddAPIKeyPopupOpen(value);
    if (!value) {
      setNewKeyName('');
      setNewKeyPermissions({
        read: true,
        write: false,
      });
    }
  };

  const toggleDeleteAPIKeyPopupModal = (value: boolean, key?: AdUpApiKey) => {
    setIsDeleteAPIKeyPopupOpen(value);
    if (value && key) {
      setSelectedKeyForDeletion(key);
    } else {
      setSelectedKeyForDeletion(null);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        // Optional: Show success message
        console.log(`Copied: ${text}`);
      })
      .catch((err) => {
        // Optional: Handle error
        console.error('Copy failed', err);
      });
  };

  const loadApiKeys = async () => {
    setLoading(true);
    try {
      const keys = await get_adup_api_keys_api_call();
      if (keys) {
        setApiKeys(keys);
      }
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) return;

    setIsCreating(true);
    try {
      const keyData: CreateAdUpApiKeyRequest = {
        name: newKeyName.trim(),
        read_scope: newKeyPermissions.read ? 1 : 0,
        write_scope: newKeyPermissions.write ? 1 : 0,
      };

      const created = await create_adup_api_key_api_call(keyData);
      if (created) {
        toggleAddAPIKeyPopupModal(false);
        setCreatedKey(created);
      }
    } catch (error) {
      console.error('Failed to create API key:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteApiKey = async () => {
    if (!selectedKeyForDeletion) return;

    setIsDeleting(true);
    try {
      const success = await delete_adup_api_key_api_call(
        selectedKeyForDeletion.id.toString()
      );
      if (success) {
        await loadApiKeys();
        toggleDeleteAPIKeyPopupModal(false);
      }
    } catch (error) {
      console.error('Failed to delete API key:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const closeCreatedKeyPopup = () => {
    setCreatedKey(null);
    loadApiKeys();
  };

  const getSecretMask = (key: AdUpApiKey) => {
    if (!key.has_secret) return '-';
    return EXTERNAL_API_KEY_MASK_DOTS + (key.secret_hint ?? '');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const getPermissionsText = (key: AdUpApiKey) => {
    if (key.read_scope && key.write_scope) {
      return t('ReadWrite');
    } else if (key.write_scope) {
      return t('Write');
    } else {
      return t('Read');
    }
  };

  const loadExternalApiTypes = async () => {
    try {
      const types = await get_external_api_types_api_call();
      if (types) {
        setExternalApiTypes(types);
      }
    } catch (error) {
      console.error('Failed to load external API types:', error);
    }
  };

  const loadExternalApiKeys = async () => {
    setExternalLoading(true);
    try {
      const keys = await get_external_api_keys_api_call();
      if (keys) {
        setExternalApiKeys(keys);
        // The stored key is never put in the input: it starts empty and only
        // holds a replacement the user types.
        setExternalKeyValues({});
      }
    } catch (error) {
      console.error('Failed to load external API keys:', error);
    } finally {
      setExternalLoading(false);
    }
  };

  const handleSaveExternalApiKey = async (type: string) => {
    if (!externalKeyValues[type]?.trim()) return;

    setIsSavingExternal(true);
    try {
      const keyData: CreateExternalApiKeyRequest = {
        type: type,
        api_key: externalKeyValues[type].trim(),
      };

      const success = await create_external_api_key_api_call(keyData);
      if (success) {
        await loadExternalApiKeys();
      }
    } catch (error) {
      console.error('Failed to save external API key:', error);
    } finally {
      setIsSavingExternal(false);
    }
  };

  const handleUpdateExternalApiKey = async (key: ExternalApiKey) => {
    const replacement = getExternalApiKeyReplacement(
      externalKeyValues[key.type],
      getExternalApiKeyMask(key)
    );
    if (!replacement) return;

    setIsSavingExternal(true);
    try {
      const keyData: UpdateExternalApiKeyRequest = {
        api_key: replacement,
      };

      const success = await update_external_api_key_api_call(
        key.id.toString(),
        keyData
      );
      if (success) {
        await loadExternalApiKeys();
      }
    } catch (error) {
      console.error('Failed to update external API key:', error);
    } finally {
      setIsSavingExternal(false);
    }
  };

  const handleDeleteExternalApiKey = async () => {
    if (!selectedExternalKeyForDeletion) return;

    setIsDeletingExternal(true);
    try {
      const success = await delete_external_api_key_api_call(
        selectedExternalKeyForDeletion.id.toString()
      );
      if (success) {
        await loadExternalApiKeys();
        setSelectedExternalKeyForDeletion(null);
      }
    } catch (error) {
      console.error('Failed to delete external API key:', error);
    } finally {
      setIsDeletingExternal(false);
    }
  };

  const getExternalApiIcon = (type: string) => {
    switch (type) {
      case 'openai':
        return openAISVG;
      case 'google_maps':
        return googleMapsSVG;
      case 'anthropic':
        return anthropicSVG;
      case 'stripe':
        return simpleCreditCardSVG;
      default:
        return keySVG;
    }
  };

  const getExternalApiIconColor = (type: string) => {
    switch (type) {
      case 'openai':
        return '#16a34a';
      case 'google_maps':
        return '#2563eb';
      case 'anthropic':
        return '#ea580c';
      case 'stripe':
        return '#9333ea';
      default:
        return '#6b7280';
    }
  };

  const getExternalApiIconBg = (type: string) => {
    switch (type) {
      case 'openai':
        return '#dcfce7';
      case 'google_maps':
        return '#dbeafe';
      case 'anthropic':
        return '#ffedd5';
      case 'stripe':
        return '#f3e8ff';
      default:
        return '#f3f4f6';
    }
  };

  const getExternalApiTitle = (type: string) => {
    if (typeof type !== 'string') {
      return '';
    }

    if (type === 'openai') {
      return 'OpenAI API Key';
    } else {
      return `${makeFirstLetterCapital(type)} API Key`;
    }
  };

  const getExternalApiDescription = (type: string) => {
    switch (type) {
      case 'openai':
        return t('UsedForGPT-5.1-AndOtherOpenAI-ModelsInTara');
      case 'google_maps':
        return t('UsedForLocationBasedAnalyticsAndMappingFeatures');
      case 'anthropic':
        return t('UsedForClaudeAIModelsAsAnAlternativeToOpenAI');
      case 'stripe':
        return t('UsedForPaymentProcessingAndSubscriptionAnalytics');
      default:
        return t('ExternalAPIServiceConfiguration');
    }
  };

  const getExternalApiPlaceholder = (type: string) => {
    switch (type) {
      case 'openai':
        return t('sk-proj-...');
      case 'google_maps':
        return t('AIza...');
      case 'anthropic':
        return t('sk-ant-...');
      case 'stripe':
        return t('sk_live_...');
      default:
        return t('EnterAPIKey');
    }
  };

  useEffect(() => {
    loadApiKeys();
    loadExternalApiTypes();
    loadExternalApiKeys();
  }, []);

  return (
    <SubPageInnerContentHolder
      title={t('APIKeys')}
      backBtnLink={HOME_ROUTE}
      /* ------Help Button------ */
      mainButtonType='white'
      mainBtnTitle={t('Help')}
      mainBtnIcon={questionSVG}
      mainBtnLoading={false}
      mainBtnLink={HELP_LINKS.settings.apiKeys}
      isMainBtnLinkExternal={true}
    >
      <div className='settings_mobile-only-top-margin'></div>
      <SettingsCommonCard
        mainTitle={t('AdUp-API-Keys')}
        subTitle={t('UseTheseKeysToAccessTheAdUp-API-FromYourApplications')}
        mainIconSvg={keySVG}
        mainIconSvgFillColor='var(--main-text-color)'
        onClick={
          canCreateApiKey
            ? () => {
                toggleAddAPIKeyPopupModal(true);
              }
            : undefined
        }
        btnIcon={plusSignSVG}
        btnText={canCreateApiKey ? t('NewAPIKey') : undefined}
      >
        {/* These are REST API keys. The Claude connector / MCP uses a
            different, per-employee key handed out on the MCP setup page, so
            say so before anyone creates a key here expecting it to work. */}
        <div className='settingsInfoBanner settingsConnectorKeyNotice'>
          <p className='settingsConnectorKeyNotice__text'>
            <strong>{t('Note')}:</strong>{' '}
            {t('AdUpApiKeysAreForTheRestApiOnlyNotConnector')}
          </p>
          <MainSquareButton
            type='button'
            value={t('GoToMcpSetup')}
            buttonType='white'
            normal={true}
            onClick={() => navigate(MCP_SETUP_PATH)}
          />
        </div>
        {/* ---------------------- */}
        {loading ? (
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <LoadingSpinner customLoadingMsg={t('LoadingAdUpAPIKeys')} />
          </div>
        ) : apiKeys.length === 0 ? (
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <p>{t('NoAPIKeysFound')}</p>
          </div>
        ) : (
          apiKeys.map((key) => (
            <div
              key={key.id}
              style={{ marginTop: '2rem' }}
              className='settingsAPIKeyItem'
            >
              <div className='settingsAPIKeyItem__left'>
                <div className='settingsAPIKeyItem__left--title'>
                  <h3>{key.name}</h3>
                  <div className={`settingsAPIKeyItem__left--title-tag `}>
                    <span>{getPermissionsText(key)}</span>
                  </div>
                </div>
                <div className='normalForm'>
                  <div className='normalForm__top'>
                    <div className='normalForm__container1'>
                      <div className='normalForm__container1--left'>
                        <BlurAndCopyInput
                          icon={<KeySVG fill='#888888' />}
                          id={`api-key-${key.id}`}
                          value={key.api_key}
                          changeListeners={[]}
                          focusListeners={[]}
                          placeholder={t('APIKey')}
                          readonly={true}
                          isCopyToClipboardEnabled={true}
                        />
                      </div>
                      <div className='normalForm__container1--right'>
                        <AINormalInput
                          id={`api-secret-${key.id}`}
                          placeholder={t('APISecret')}
                          value={getSecretMask(key)}
                          readonly={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <p>
                  {t('Created')} {formatDate(key.created_at)} • {t('LastUsed')}{' '}
                  {formatDate(key.updated_at)}
                </p>
              </div>
              <div className='settingsAPIKeyItem__right'>
                <div
                  onClick={() => toggleDeleteAPIKeyPopupModal(true, key)}
                  className='settingsAPIKeyItem__right--deleteBtn'
                >
                  <CustomSVGs svg={trash2SVG} />
                </div>
              </div>
            </div>
          ))
        )}
        {/* ---------------------- */}
      </SettingsCommonCard>
      {/* -------------- */}
      {/* <SettingsCommonCard // TODO - BE not ready yet
        mainTitle={t('InternalServiceKeys')}
        subTitle={t('ManageAccessToAdUpsInternalAIservicesAndFeatures')}
        styles={{ marginTop: '3rem' }}
      >
        <>
          <div style={{ marginTop: '2rem' }} className='itemWithRightAction'>
            <div className='itemWithRightAction__left'>
              <div className='itemWithRightAction__left--title'>
                <h3>{t('AIOptimize')}</h3>
                <div
                  className={`itemWithRightAction__left--title-tag itemWithRightAction__left--title-tag-${'active'}`}
                >
                  <span>{t('Active')}</span>
                </div>
              </div>
              <p>{t('AutomatedCampaignOptimizationUsingMachineLearning')}</p>
            </div>
            <div className='itemWithRightAction__right'>
              <div className='itemWithRightAction__right--greenToggle'>
                <ToggleSwitch
                  isOn={true}
                  onClick={() => {}}
                  id='AI-Optimize-Switch'
                  disabled={false}
                />
              </div>
            </div>
          </div>
          <div style={{ marginTop: '2rem' }} className='itemWithRightAction'>
            <div className='itemWithRightAction__left'>
              <div className='itemWithRightAction__left--title'>
                <h3>{t('AIInsights')}</h3>
                <div
                  className={`itemWithRightAction__left--title-tag itemWithRightAction__left--title-tag-${'active'}`}
                >
                  <span>{t('Active')}</span>
                </div>
              </div>
              <p>{t('AdvancedAnalyticsAndPredictiveInsights')}</p>
            </div>
            <div className='itemWithRightAction__right'>
              <div className='itemWithRightAction__right--greenToggle'>
                <ToggleSwitch
                  isOn={true}
                  onClick={() => {}}
                  id='AI-Optimize-Switch'
                  disabled={false}
                />
              </div>
            </div>
          </div>
          <div style={{ marginTop: '2rem' }} className='itemWithRightAction'>
            <div className='itemWithRightAction__left'>
              <div className='itemWithRightAction__left--title'>
                <h3>{t('SmartBidding')}</h3>
                <div
                  className={`itemWithRightAction__left--title-tag itemWithRightAction__left--title-tag-${'inactive'}`}
                >
                  <span>{t('Disabled')}</span>
                </div>
              </div>
              <p>{t('IntelligentBidManagementAndOptimization')}</p>
            </div>
            <div className='itemWithRightAction__right'>
              <div className='itemWithRightAction__right--greenToggle'>
                <ToggleSwitch
                  isOn={false}
                  onClick={() => {}}
                  id='Smart-Bidding-Switch'
                  disabled={false}
                />
              </div>
            </div>
          </div>
          <div style={{ marginTop: '2rem' }} className='itemWithRightAction'>
            <div className='itemWithRightAction__left'>
              <div className='itemWithRightAction__left--title'>
                <h3>{t('AudienceAI')}</h3>
                <div
                  className={`itemWithRightAction__left--title-tag itemWithRightAction__left--title-tag-${'active'}`}
                >
                  <span>{t('Active')}</span>
                </div>
              </div>
              <p>{t('AIPoweredAudienceTargetingAndSegmentation')}</p>
            </div>
            <div className='itemWithRightAction__right'>
              <div className='itemWithRightAction__right--greenToggle'>
                <ToggleSwitch
                  isOn={true}
                  onClick={() => {}}
                  id='Audience-AI-Switch'
                  disabled={false}
                />
              </div>
            </div>
          </div>
          <div style={{ marginTop: '2rem' }} className='settingsInfoBanner'>
            <strong>{t('Note')}:</strong> {t('InternalServiceKeysBannerText')}
          </div>
        </>
      </SettingsCommonCard> */}
      {/* -------------- */}
      <SettingsCommonCard
        mainTitle={t('ExternalAPI-Keys')}
        subTitle={t(
          'Configure-API-KeysForExternalServicesUsedByTaraAIassistant'
        )}
        styles={{ marginTop: '3rem' }}
      >
        {externalLoading ? (
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <LoadingSpinner customLoadingMsg={t('LoadingExternalAPIKeys')} />
          </div>
        ) : (
          externalApiTypes.map((apiType) => {
            const existingKey = externalApiKeys.find(
              (key) => key.type === apiType.slug
            );
            const currentValue = externalKeyValues[apiType.slug] || '';
            const storedKeyMask = getExternalApiKeyMask(existingKey);
            const replacement = getExternalApiKeyReplacement(
              currentValue,
              storedKeyMask
            );

            return (
              <div key={apiType.slug} className='externalAPIKeyItem'>
                <div className='externalAPIKeyItem__title'>
                  <div
                    style={{
                      marginRight: '1rem',
                      backgroundColor: getExternalApiIconBg(apiType.slug),
                    }}
                    className='externalAPIKeyItem__title--icon'
                  >
                    <CustomSVGs
                      fillColor={getExternalApiIconColor(apiType.slug)}
                      svg={getExternalApiIcon(apiType.slug)}
                    />
                  </div>
                  <h3>{getExternalApiTitle(apiType.slug)}</h3>
                </div>
                {existingKey && storedKeyMask && (
                  <div
                    className='externalAPIKeyItem__input'
                    data-testid={`external-api-${apiType.slug}-stored`}
                  >
                    <AINormalInput
                      id={`external-api-${apiType.slug}-stored`}
                      placeholder={t('ExternalApiKeyStored')}
                      value={storedKeyMask}
                      readonly={true}
                    />
                  </div>
                )}
                <div className='externalAPIKeyItem__input'>
                  <AINormalInput
                    changeListeners={[
                      (event) => {
                        setExternalKeyValues((prev) => ({
                          ...prev,
                          [apiType.slug]: event.target.value,
                        }));
                      },
                    ]}
                    id={`external-api-${apiType.slug}`}
                    type={existingKey ? 'password' : 'text'}
                    placeholder={
                      existingKey ? t('ExternalApiKeyReplace') : undefined
                    }
                    value={currentValue}
                    innerPlaceholder={getExternalApiPlaceholder(apiType.slug)}
                    disabled={isSavingExternal}
                  />
                </div>
                <div className='externalAPIKeyItem__description'>
                  {getExternalApiDescription(apiType.slug)}
                </div>
                <div
                  className='externalAPIKeyItem__actions'
                  style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}
                >
                  {existingKey ? (
                    <>
                      <MainSquareButton
                        value={t('Update')}
                        type='button'
                        disabled={
                          isSavingExternal || !replacement || isDeletingExternal
                        }
                        onClick={() => handleUpdateExternalApiKey(existingKey)}
                      />
                      <MainSquareButton
                        value={t('Delete')}
                        type='button'
                        buttonType='delete'
                        disabled={isDeletingExternal || isSavingExternal}
                        onClick={() =>
                          setSelectedExternalKeyForDeletion(existingKey)
                        }
                      />
                    </>
                  ) : (
                    <MainSquareButton
                      value={t('Save')}
                      type='button'
                      disabled={isSavingExternal || !currentValue.trim()}
                      onClick={() => handleSaveExternalApiKey(apiType.slug)}
                    />
                  )}
                </div>
              </div>
            );
          })
        )}
      </SettingsCommonCard>

      <Popup
        styles={{ backgroundColor: '#fff' }}
        setTrigger={() => toggleAddAPIKeyPopupModal(false)}
        trigger={isAddAPIKeyPopupOpen}
      >
        <div className='addAPIKey'>
          <h2 className='addAPIKey__title'>{t('CreateNewAPIKey')}</h2>
          <div className='addAPIKey__inputs'>
            {/* <SettingsIconSelectInput
              filterOptions={[
                { text: t('AdUpAPI-Key'), value: 'adup_api_key', icon: null },
                {
                  text: t('ExternalServiceKey'),
                  value: 'external_service',
                  icon: null,
                },
              ]}
              value={'adup_api_key'}
              onFilterChange={(val) => {}}
              title={t('KeyType')}
            /> */}
            <AINormalInput
              changeListeners={[(event) => setNewKeyName(event.target.value)]}
              id='CustomEvent__event-name-input'
              placeholder={t('Name')}
              value={newKeyName}
              required={true}
              innerPlaceholder={t('ProductionAPI-Key')}
            />

            <div className='addAPIKey__inputs--permissions'>
              <h3>{t('Permissions')}</h3>
              <RadioButton
                name='read'
                label={t('Read')}
                value={'read'}
                checked={newKeyPermissions.read}
                onChange={() =>
                  setNewKeyPermissions((prev) => ({
                    ...prev,
                    read: !prev.read,
                  }))
                }
                disabled={false}
              />
              <RadioButton
                name='write'
                label={t('Write')}
                value={'write'}
                checked={newKeyPermissions.write}
                onChange={() =>
                  setNewKeyPermissions((prev) => ({
                    ...prev,
                    write: !prev.write,
                  }))
                }
                disabled={false}
              />
            </div>
          </div>
          <div className='addAPIKey__buttons'>
            <MainSquareButton
              value={t('Cancel')}
              type='button'
              buttonType='white'
              disabled={false}
              onClick={() => {
                toggleAddAPIKeyPopupModal(false);
              }}
            />
            <MainSquareButton
              value={t('GenerateKey')}
              type='button'
              disabled={
                isCreating ||
                !newKeyName.trim() ||
                (!newKeyPermissions.read && !newKeyPermissions.write)
              }
              onClick={handleCreateApiKey}
            />
          </div>
        </div>
      </Popup>

      <Popup
        styles={{ backgroundColor: '#fff' }}
        setTrigger={() => toggleDeleteAPIKeyPopupModal(false)}
        trigger={isDeleteAPIKeyPopupOpen}
      >
        <div className='deleteAPIKey'>
          <div className='deleteAPIKey--title'>
            <div className='deleteAPIKey--title--icon'>
              <CustomSVGs svg={warningWithCircleSVG} />
            </div>
            <h3>{t('DeleteAPIKey')}</h3>
          </div>
          <div className='deleteAPIKey--message'>
            <p>{t('AreYouSureYouWantToDeleteThisAPIKey')}</p>
            {selectedKeyForDeletion && (
              <p>
                <strong>{t('Key')}:</strong> {selectedKeyForDeletion.name} (
                {truncate(selectedKeyForDeletion.api_key, 20)})
              </p>
            )}
          </div>
          <div className='deleteAPIKey--buttons'>
            <MainSquareButton
              value={t('Cancel')}
              type='button'
              buttonType='white'
              disabled={false}
              onClick={() => {
                toggleDeleteAPIKeyPopupModal(false);
              }}
            />
            <MainSquareButton
              value={t('DeleteKey')}
              type='button'
              buttonType='delete'
              disabled={isDeleting}
              onClick={handleDeleteApiKey}
            />
          </div>
        </div>
      </Popup>

      <Popup
        styles={{ backgroundColor: '#fff' }}
        setTrigger={closeCreatedKeyPopup}
        trigger={!!createdKey}
      >
        {createdKey && (
          <div className='addAPIKey'>
            <h2 className='addAPIKey__title'>{t('APIKeyCreated')}</h2>
            <div className='settingsInfoBanner'>
              <strong>{t('APISecretShownOnceWarning')}</strong>
            </div>
            <div className='addAPIKey__inputs normalForm'>
              <BlurAndCopyInput
                icon={<KeySVG fill='#888888' />}
                id='created-api-key'
                value={createdKey.api_key}
                changeListeners={[]}
                focusListeners={[]}
                placeholder={t('APIKey')}
                readonly={true}
                isCopyToClipboardEnabled={true}
              />
              <BlurAndCopyInput
                icon={<SecretSVG fill='#888888' />}
                id='created-api-secret'
                value={createdKey.api_secret}
                changeListeners={[]}
                focusListeners={[]}
                placeholder={t('APISecret')}
                readonly={true}
                isCopyToClipboardEnabled={true}
              />
            </div>
            <div className='addAPIKey__buttons'>
              <MainSquareButton
                value={t('Done')}
                type='button'
                onClick={closeCreatedKeyPopup}
              />
            </div>
          </div>
        )}
      </Popup>

      {/* External API Key Delete Confirmation Popup */}
      <Popup
        styles={{ backgroundColor: '#fff' }}
        setTrigger={() => setSelectedExternalKeyForDeletion(null)}
        trigger={!!selectedExternalKeyForDeletion}
      >
        <div className='deleteAPIKey'>
          <div className='deleteAPIKey--title'>
            <div className='deleteAPIKey--title--icon'>
              <CustomSVGs svg={warningWithCircleSVG} />
            </div>
            <h3>{t('DeleteAPIKey')}</h3>
          </div>
          <div className='deleteAPIKey--message'>
            <p>{t('AreYouSureYouWantToDeleteThisAPIKey')}</p>
            {selectedExternalKeyForDeletion && (
              <p>
                <strong>{t('Key')}:</strong>{' '}
                {selectedExternalKeyForDeletion.name} (
                {getExternalApiKeyMask(selectedExternalKeyForDeletion) ?? ''})
              </p>
            )}
          </div>
          <div className='deleteAPIKey--buttons'>
            <MainSquareButton
              value={t('Cancel')}
              type='button'
              buttonType='white'
              disabled={isDeletingExternal}
              onClick={() => setSelectedExternalKeyForDeletion(null)}
            />
            <MainSquareButton
              value={t('DeleteKey')}
              type='button'
              buttonType='delete'
              disabled={isDeletingExternal}
              onClick={handleDeleteExternalApiKey}
            />
          </div>
        </div>
      </Popup>
    </SubPageInnerContentHolder>
  );
};

export default APIKeySettings;
